/**
 * Auth contra BridgeHub (JWT propio del BFF). En dev pega a `/bridge/auth/login`
 * (proxy de Vite → localhost:8000). El token se guarda en localStorage.
 *
 * NOTA: localStorage es accesible por XSS — válido para este panel interno, pero
 * si más adelante se endurece, migrar a httpOnly cookie + refresh rotativo.
 */
import type { CompanyRef } from '@contracts';
import { BRIDGE_URL as BRIDGE } from '@/lib/config';

const TOKEN_KEY = 'bh_token';
const USER_KEY = 'bh_user';
const ROLE_KEY = 'bh_role';
const REFRESH_KEY = 'bh_refresh';
const COMPANIES_KEY = 'bh_companies';
const SUPERADMIN_KEY = 'bh_superadmin';
const USER_ID_KEY = 'bh_uid';
const ALLOWEDPOS_KEY = 'bh_allowedpos';
// Cache global legacy del layout (se reemplazó por uno por-usuario en dashboardContext).
const LEGACY_LAYOUT_KEY = 'portal_dashboard_views';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Renueva el access token con el refresh token (BridgeHub `/auth/refresh`, que
 * rota ambos). Devuelve true si renovó. Deduplicado: varias 401 concurrentes
 * comparten una sola llamada de refresh.
 */
let refreshing: Promise<boolean> | null = null;
export function refreshAccessToken(): Promise<boolean> {
  if (refreshing) return refreshing;
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return Promise.resolve(false);
  refreshing = (async () => {
    try {
      const res = await fetch(`${BRIDGE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      const body = await res.json();
      if (!body?.success || !body?.data?.accessToken) return false;
      localStorage.setItem(TOKEN_KEY, body.data.accessToken);
      if (body.data.refreshToken) localStorage.setItem(REFRESH_KEY, body.data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export function getUserName(): string {
  return localStorage.getItem(USER_KEY) || 'Usuario';
}

export function getRole(): string {
  return (localStorage.getItem(ROLE_KEY) || '').toLowerCase();
}

/** El builder de métricas personalizadas es solo para staff (no 'client'/portal). */
export function isAdmin(): boolean {
  return !['client', 'portal', ''].includes(getRole());
}

/** Empresas a las que el usuario tiene acceso (del JWT de BridgeHub). */
export function getCompanies(): CompanyRef[] {
  try {
    const raw = localStorage.getItem(COMPANIES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Super admin (= base.group_system): ve todas las empresas y PDV. El scope real
 * lo impone el BFF; esto es solo para que el front muestre/oculte filtros. */
export function isSuperAdmin(): boolean {
  return localStorage.getItem(SUPERADMIN_KEY) === '1';
}

/**
 * Tier "admin" del dashboard: super-admin (group_system) O acceso a >1 empresa.
 * Mismo criterio que `dashboard_tier` del BFF (admin = role admin | >1 empresa).
 * Gatea acciones de gestión del tablero (ej. agregar widgets personalizados).
 */
export function isDashboardAdmin(): boolean {
  return isSuperAdmin() || getCompanies().length > 1;
}

/** PDV asignados al usuario (`allowedPos` del JWT). Vacío = sin PDV. */
export function getAllowedPos(): number[] {
  try {
    const raw = localStorage.getItem(ALLOWEDPOS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'number') : [];
  } catch {
    return [];
  }
}

/**
 * Tier del usuario (mismo criterio que `dashboard_tier` del BFF):
 * admin = super-admin | >1 empresa; regional = 1 empresa + ≥2 PDV; gerente = 1 empresa + 1 PDV.
 */
export function dashboardTier(): 'admin' | 'regional' | 'gerente' {
  if (isDashboardAdmin()) return 'admin';
  if (getCompanies().length === 1 && getAllowedPos().length >= 2) return 'regional';
  return 'gerente';
}

/** Solo admin/regional pueden abrir los deep-links a Odoo desde los drill-downs (los
 *  gerentes de tienda no navegan a los registros de Odoo). */
export function canOpenOdooLinks(): boolean {
  return dashboardTier() !== 'gerente';
}

/** Id del usuario (sub del JWT). Sirve para cachear el layout por-usuario. */
export function getUserId(): string {
  return localStorage.getItem(USER_ID_KEY) || '';
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(COMPANIES_KEY);
  localStorage.removeItem(SUPERADMIN_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(ALLOWEDPOS_KEY);
  // Limpia el cache global legacy para que ningún usuario herede el layout de otro.
  localStorage.removeItem(LEGACY_LAYOUT_KEY);
}

export async function login(email: string, password: string, companySlug: string): Promise<void> {
  const res = await fetch(`${BRIDGE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `app: 'dashboard'` marca la sesión como SOLO LECTURA en BridgeHub: no puede
    // escribir catálogo aunque el usuario sea admin (seguridad ante robo de sesión).
    body: JSON.stringify({ email, password, companySlug, app: 'dashboard' }),
  });
  const body = await res.json();
  if (!body.success) throw new Error(body.message || 'No se pudo iniciar sesión');
  const token = body?.data?.accessToken;
  if (!token) throw new Error('Respuesta sin accessToken');
  localStorage.setItem(TOKEN_KEY, token);
  const refresh = body?.data?.refreshToken;
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  const name = body?.data?.user?.name;
  if (name) localStorage.setItem(USER_KEY, name);
  const role = body?.data?.user?.role;
  if (role) localStorage.setItem(ROLE_KEY, String(role));
  const companies = body?.data?.user?.companies;
  localStorage.setItem(COMPANIES_KEY, JSON.stringify(Array.isArray(companies) ? companies : []));
  localStorage.setItem(SUPERADMIN_KEY, body?.data?.user?.isSuperAdmin ? '1' : '0');
  const allowedPos = body?.data?.user?.allowedPos;
  localStorage.setItem(ALLOWEDPOS_KEY, JSON.stringify(Array.isArray(allowedPos) ? allowedPos : []));
  const uid = body?.data?.user?.id;
  if (uid != null) localStorage.setItem(USER_ID_KEY, String(uid));
}
